import { useCallback, useEffect, useRef, useState } from 'react';
import { useHover, useInteractOutside } from 'react-aria';
import cn from 'classnames';
import { Cell, HStack, Link, Text, TextCell, useHStack } from '@direct-frontend/components';
import { genericMemo, useIsomorphicLayoutEffect, useToggle } from '@direct-frontend/stdlib';
import { Expander } from './components/Expander/Expander';
import { LevelsBlank } from './components/LevelsBlank';
import { type TreeNodeProps } from './lib/types';
import { type SubscribeHandler, useContainerTreeSizeSubscribe } from '../ContainerTreeSizeProvider';
import styles from './TreeNode.module.css';

export const TreeNode = genericMemo(function TreeNode<Attributes = unknown>({
    'data-testid': dataTestId,
    size,
    node,
    beforeAddon,
    controls,
    addonRight,
    isOpen,
    isOpenDisabled,
    isSelected,
    onToggle,
    children,
    href,
    onClick,
    isDnDStub,
    isTruncated,
    content,
    ignoreHover,
    className: classNameProps,
    hoverClassName,
    withoutTruncate,
    controlsContainerClassName,
}: TreeNodeProps<Attributes>) {
    const className = useHStack({});

    const { hoverProps, isHovered } = useHover({});

    const refContent = useRef<HTMLDivElement>(null);
    const refBeforeAddon = useRef<HTMLDivElement>(null);
    const refAfterAddonOver = useRef<HTMLDivElement>(null);
    const refControls = useRef<HTMLDivElement>(null);
    const refAddonRight = useRef<HTMLDivElement>(null);

    const [isNeedToTruncated, setIsNeedToTruncated] = useState(false);

    const isOver = !isDnDStub && (isHovered || isTruncated) && isNeedToTruncated && !withoutTruncate;

    const handleContainerTreeSize = useCallback<SubscribeHandler>(
        ({ isInitialed, width: containerWidth, left: containerLeft }) => {
            // Если стаб для переноса, то ничего не считаем
            if (isDnDStub) {
                return;
            }

            // Если контекст размера дерева не инициализирован, то ничего не считаем
            if (!isInitialed) {
                return;
            }

            // Если не подсвечен и не включен truncated, то ничего не считаем
            if (!isHovered && !isTruncated) {
                return;
            }

            if (refAfterAddonOver.current && refContent.current && refControls.current && refAddonRight.current) {
                const itemRect = refContent.current.getBoundingClientRect();
                const controlsRect = refControls.current.getBoundingClientRect();
                const addonRightRect = refAddonRight.current.getBoundingClientRect();

                const activeRect = isHovered ? controlsRect : addonRightRect;

                if (
                    containerWidth < itemRect.width + (itemRect.left - containerLeft) + activeRect.width &&
                    itemRect.left < containerLeft + containerWidth
                ) {
                    // Если ширина контента с отступом больше ширины контейнера минус ширину контролов
                    // и положение контента в пределах видимой области контейнера,
                    // то подменяем на truncated версию
                    setIsNeedToTruncated(true);
                } else {
                    setIsNeedToTruncated(false);

                    return;
                }

                let width = activeRect.left + activeRect.width - itemRect.left;

                if (!refAfterAddonOver.current.style.width && refAddonRight.current.children.length) {
                    width = activeRect.left + 38 - itemRect.left;
                }

                if (!refAfterAddonOver.current.style.width && !node.hasChildren && !node.parentId) {
                    width += 105;
                }

                if (!refAfterAddonOver.current.style.width && !node.hasChildren && node.parentId) {
                    width += 44;
                }

                refAfterAddonOver.current.style.width = `${width}px`;
            }
        },
        [isDnDStub, isHovered, isTruncated, node.hasChildren, node.parentId]
    );

    useContainerTreeSizeSubscribe(handleContainerTreeSize);

    const hasBefore = Boolean(beforeAddon);

    useIsomorphicLayoutEffect(() => {
        if (hasBefore && refBeforeAddon.current && refContent.current) {
            refContent.current.style.left = `${refBeforeAddon.current.offsetWidth}px`;
        }
    }, [hasBefore]);

    const hStack = useHStack({ justifyContent: 'flex-end', alignItems: 'center' });

    const stateClassName = cn({
        [styles.selected]: isSelected,
        [styles.hovered]: !ignoreHover && !isDnDStub && isHovered,
    });

    const {
        value: areControlsClicked,
        toggleOn: setControlsClicked,
        toggleOff: setControlsNotClicked,
    } = useToggle(false);

    useInteractOutside({ ref: refControls, onInteractOutside: setControlsNotClicked });

    useEffect(() => {
        const controls = refControls.current;

        const onClick = () => {
            setControlsClicked();
        };

        controls?.addEventListener('click', onClick);

        return () => {
            controls?.removeEventListener('click', onClick);
        };
    }, [areControlsClicked, setControlsClicked]);

    return (
        <div
            data-testid={dataTestId}
            {...hoverProps}
            className={cn(styles.container, className, stateClassName, classNameProps, isHovered && hoverClassName)}
            onClick={onClick}
        >
            {Boolean(beforeAddon) && (
                <div ref={refBeforeAddon} className={styles.beforeAddon}>
                    <div>{beforeAddon}</div>
                </div>
            )}
            <HStack>
                <LevelsBlank node={node} size={size} />
                {node.hasChildren && (
                    <Expander
                        className={cn(styles.before, {
                            [styles.hidden]: isOver,
                        })}
                        data-testid={`${dataTestId}.Expander`}
                        isDisabled={isOpenDisabled}
                        isOpen={isOpen}
                        onClick={() => {
                            onToggle?.(node.id);
                        }}
                    />
                )}
            </HStack>
            {children && (
                <HStack className={styles.contentWrapper} flex="1 1 auto">
                    <Cell
                        ref={refContent}
                        className={cn(styles.content, styles.noWrap, {
                            [styles.clickable]: Boolean(onClick),
                            [styles.hidden]: isOver,
                        })}
                        isGrowable={false}
                        size={size}
                        onClick={onClick}
                        width={content ? 'full' : undefined}
                    >
                        <Link href={href} color="main" onClick={href ? undefined : onClick}>
                            <Text size={size === 'm' ? 's' : 'xs'}>{children}</Text>
                        </Link>
                    </Cell>
                </HStack>
            )}
            {content}
            {href && <Link className={styles.endRow} href={href} onClick={onClick} />}
            <div className={cn(styles.afterAddon, controlsContainerClassName)}>
                <div ref={refAfterAddonOver} className={cn(hStack, styles.over)}>
                    {node.hasChildren && isOver && (
                        <Expander
                            data-testid={`${dataTestId}.Expander`}
                            isDisabled={isOpenDisabled}
                            isOpen={isOpen}
                            onClick={() => onToggle && onToggle(node.id)}
                        />
                    )}
                    {isOver && (
                        <TextCell
                            rightPadding={false}
                            className={cn({ [styles.clickable]: Boolean(onClick) })}
                            onClick={onClick}
                            isTruncated
                            title={typeof children === 'string' ? children : ''}
                            size={size}
                        />
                    )}
                    <div ref={refControls} className={cn({ [styles.removed]: !isHovered && !areControlsClicked })}>
                        {controls}
                    </div>
                    <div ref={refAddonRight} className={cn({ [styles.removed]: isHovered || areControlsClicked })}>
                        {addonRight}
                    </div>
                </div>
            </div>
        </div>
    );
});
